'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Users, Briefcase, TrendingUp, Plus, Search, Mail, Phone, MoreHorizontal, CheckCircle2, Clock, XCircle,  } from 'lucide-react';

interface Employee {
  id: string;
  name: string;
  avatar: string;
  role: string;
  department: string;
  email: string;
  phone: string;
  status: 'active' | 'on-leave' | 'inactive';
  type: 'full-time' | 'part-time' | 'contractor';
  joined: string;
  performance: number;
  manager: string;
}

const employees: Employee[] = [
  { id: 'e1', name: 'Ryan Ashford', avatar: 'RA', role: 'Super Admin', department: 'Management', email: 'ryan@arrowsgroups.com', phone: '+971 50 100 0001', status: 'active', type: 'full-time', joined: 'Jan 2020', performance: 98, manager: '—' },
  { id: 'e2', name: 'Layla Khalid', avatar: 'LK', role: 'BPO Team Lead', department: 'BPO Center', email: 'layla@arrowsgroups.com', phone: '+971 50 100 0002', status: 'active', type: 'full-time', joined: 'Mar 2021', performance: 94, manager: 'Ryan A.' },
  { id: 'e3', name: 'Omar Saeed', avatar: 'OS', role: 'Account Manager', department: 'Sales', email: 'omar@arrowsgroups.com', phone: '+971 50 100 0003', status: 'active', type: 'full-time', joined: 'Jun 2021', performance: 88, manager: 'Ryan A.' },
  { id: 'e4', name: 'Nadia Hassan', avatar: 'NH', role: 'BPO Agent', department: 'BPO Center', email: 'nadia@arrowsgroups.com', phone: '+971 50 100 0004', status: 'active', type: 'full-time', joined: 'Sep 2022', performance: 91, manager: 'Layla K.' },
  { id: 'e5', name: 'Khalid Mansoor', avatar: 'KM', role: 'BPO Agent', department: 'BPO Center', email: 'khalid@arrowsgroups.com', phone: '+971 50 100 0005', status: 'on-leave', type: 'full-time', joined: 'Nov 2022', performance: 85, manager: 'Layla K.' },
  { id: 'e6', name: 'Sara Al-Farsi', avatar: 'SF', role: 'Digital Marketing Exec', department: 'Marketing', email: 'sara@arrowsgroups.com', phone: '+971 50 100 0006', status: 'active', type: 'full-time', joined: 'Feb 2023', performance: 92, manager: 'Ryan A.' },
  { id: 'e7', name: 'Rami Yousef', avatar: 'RY', role: 'Web Developer', department: 'Web Development', email: 'rami@arrowsgroups.com', phone: '+971 50 100 0007', status: 'active', type: 'contractor', joined: 'Apr 2023', performance: 89, manager: 'Ryan A.' },
  { id: 'e8', name: 'Hana Qasim', avatar: 'HQ', role: 'Finance Officer', department: 'Finance', email: 'hana@arrowsgroups.com', phone: '+971 50 100 0008', status: 'active', type: 'full-time', joined: 'Jul 2023', performance: 96, manager: 'Ryan A.' },
  { id: 'e9', name: 'Tariq Bilal', avatar: 'TB', role: 'VOIP Engineer', department: 'VOIP', email: 'tariq@arrowsgroups.com', phone: '+971 50 100 0009', status: 'inactive', type: 'part-time', joined: 'Jan 2024', performance: 72, manager: 'Ryan A.' },
];

const departments = ['All', 'Management', 'BPO Center', 'Sales', 'Marketing', 'Web Development', 'Finance', 'VOIP'];

const statusConfig = {
  'active':   { label: 'Active',    color: 'text-success', bg: 'bg-success/10', icon: CheckCircle2 },
  'on-leave': { label: 'On Leave',  color: 'text-warning', bg: 'bg-warning/10', icon: Clock },
  'inactive': { label: 'Inactive',  color: 'text-muted-foreground', bg: 'bg-muted', icon: XCircle },
};

const typeConfig = {
  'full-time':  { label: 'Full-Time',  color: 'text-primary', bg: 'bg-primary/10' },
  'part-time':  { label: 'Part-Time',  color: 'text-amber-400', bg: 'bg-amber-400/10' },
  'contractor': { label: 'Contractor', color: 'text-muted-foreground', bg: 'bg-muted' },
};

export default function EmployeesPage() {
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('All');

  const filtered = employees.filter((e) => {
    const matchSearch = e.name.toLowerCase().includes(search.toLowerCase()) || e.role.toLowerCase().includes(search.toLowerCase());
    const matchDept = deptFilter === 'All' || e.department === deptFilter;
    return matchSearch && matchDept;
  });

  const activeCount = employees.filter(e => e.status === 'active').length;
  const onLeaveCount = employees.filter(e => e.status === 'on-leave').length;
  const avgPerformance = Math.round(employees.reduce((s, e) => s + e.performance, 0) / employees.length);

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">Employees</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Manage staff directory, roles, and performance</p>
          </div>
          <button className="flex items-center gap-2 bg-primary text-primary-foreground px-3 sm:px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex-shrink-0">
            <Plus size={16} />
            <span className="hidden sm:inline">Add Employee</span>
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {[
            { label: 'Total Staff', value: employees.length, sub: `${activeCount} currently active`, icon: Users, color: 'text-primary', bg: 'bg-primary/10' },
            { label: 'On Leave', value: onLeaveCount, sub: 'Approved absences', icon: Clock, color: 'text-warning', bg: 'bg-warning/10' },
            { label: 'Avg Performance', value: `${avgPerformance}%`, sub: 'Across all departments', icon: TrendingUp, color: 'text-success', bg: 'bg-success/10' },
            { label: 'Departments', value: departments.length - 1, sub: 'Active business units', icon: Briefcase, color: 'text-amber-400', bg: 'bg-amber-400/10' },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-card border border-border rounded-xl p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <span className="text-xs sm:text-sm text-muted-foreground">{kpi.label}</span>
                <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon size={16} className={kpi.color} />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-foreground">{kpi.value}</p>
              <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{kpi.sub}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-3 sm:p-4 mb-4">
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name or role..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {departments.map((d) => (
                <button
                  key={d}
                  onClick={() => setDeptFilter(d)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${deptFilter === d ? 'bg-primary text-primary-foreground' : 'bg-background border border-border text-muted-foreground hover:text-foreground'}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Employee</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Department</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Performance</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Manager</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Joined</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((emp) => {
                  const sc = statusConfig[emp.status];
                  const tc = typeConfig[emp.type];
                  const StatusIcon = sc.icon;
                  return (
                    <tr key={emp.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-primary">{emp.avatar}</span>
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{emp.name}</p>
                            <p className="text-xs text-muted-foreground">{emp.role}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{emp.department}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                          <StatusIcon size={11} />
                          {sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${tc.bg} ${tc.color}`}>{tc.label}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden w-20">
                            <div
                              className={`h-full rounded-full ${emp.performance >= 90 ? 'bg-success' : emp.performance >= 75 ? 'bg-warning' : 'bg-danger'}`}
                              style={{ width: `${emp.performance}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-foreground">{emp.performance}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{emp.manager}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{emp.joined}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"><Mail size={14} /></button>
                          <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"><Phone size={14} /></button>
                          <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"><MoreHorizontal size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="py-16 text-center text-muted-foreground text-sm">No employees match your search.</div>
            )}
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden flex flex-col gap-3">
          {filtered.map((emp) => {
            const sc = statusConfig[emp.status];
            const tc = typeConfig[emp.type];
            const StatusIcon = sc.icon;
            return (
              <div key={emp.id} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-primary">{emp.avatar}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground text-sm">{emp.name}</p>
                      <p className="text-xs text-muted-foreground">{emp.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground"><Mail size={14} /></button>
                    <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground"><MoreHorizontal size={14} /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                    <StatusIcon size={10} />{sc.label}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tc.bg} ${tc.color}`}>{tc.label}</span>
                  <span className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">{emp.department}</span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${emp.performance >= 90 ? 'bg-success' : emp.performance >= 75 ? 'bg-warning' : 'bg-danger'}`}
                      style={{ width: `${emp.performance}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-foreground">{emp.performance}%</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Mgr: {emp.manager}</span>
                  <span>Joined: {emp.joined}</span>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-16 text-center text-muted-foreground text-sm">No employees match your search.</div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
